import React, { useEffect, useState } from "react";
import { http } from "../lib/api";
import { ESTADOS_COTIZACION } from "../lib/constants";
import { toast } from "sonner";
import { X } from "lucide-react";

export default function AdminCotizaciones() {
  const [tab, setTab] = useState("comunidad"); // comunidad | residente
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/admin/cotizaciones/${tab}`);
      setItems(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab]);

  const updateEstado = async (id, estado) => {
    try {
      await http.patch(`/admin/cotizaciones/${tab}/${id}`, { estado });
      toast.success("Estado actualizado");
      load();
      setSelected((s) => s ? { ...s, estado } : s);
    } catch { toast.error("No se pudo actualizar"); }
  };

  return (
    <div data-testid="admin-cotizaciones">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Cotizaciones</h1>
        <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Solicitudes enviadas desde el sitio.</p>
      </div>

      <div className="flex gap-2 mb-6">
        {["comunidad","residente"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`yn-btn ${tab === t ? "yn-btn--active-yes" : ""}`} data-testid={`tab-${t}`}>
            {t === "comunidad" ? "Comunidad (B2B)" : "Residentes (B2C)"}
          </button>
        ))}
      </div>

      <div className="bg-white border rounded-xl overflow-hidden" style={{ borderColor: "var(--border-subtle)" }}>
        {loading ? (
          <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>No hay cotizaciones.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                {tab === "comunidad" ? (
                  <tr><th>Comunidad</th><th>Contacto</th><th>Comuna</th><th>UF estimado</th><th>Estado</th><th>Fecha</th><th></th></tr>
                ) : (
                  <tr><th>Producto</th><th>Contacto</th><th>RUT</th><th>Email</th><th>Estado</th><th>Fecha</th><th></th></tr>
                )}
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id}>
                    {tab === "comunidad" ? (
                      <>
                        <td className="font-semibold">{c.razon_social}</td>
                        <td>{c.nombre} {c.apellido}<div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{c.email}</div></td>
                        <td>{c.comuna}</td>
                        <td>{c.prima_estimada_uf ? `UF ${c.prima_estimada_uf.toFixed(1)}` : "—"}</td>
                      </>
                    ) : (
                      <>
                        <td className="font-semibold capitalize">{c.producto?.replace(/_/g, " ")}</td>
                        <td>{c.nombre} {c.apellido}<div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{c.telefono}</div></td>
                        <td>{c.rut}</td>
                        <td>{c.email}</td>
                      </>
                    )}
                    <td><span className={`badge badge--${c.estado || "nueva"}`}>{c.estado || "nueva"}</span></td>
                    <td className="text-[12px]" style={{ color: "var(--text-muted)" }}>{(c.created_at || "").slice(0,10)}</td>
                    <td><button className="text-sm font-semibold" style={{ color: "var(--brand-accent)" }} onClick={() => setSelected(c)} data-testid={`view-${c.id}`}>Ver</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(10,37,64,0.55)" }} onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="cotizacion-modal">
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
              <div>
                <h3 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Detalle cotización</h3>
                <div className="text-[11px] font-mono mt-0.5" style={{ color: "var(--text-muted)" }}>{selected.id}</div>
              </div>
              <button onClick={() => setSelected(null)}><X size={20}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-2" style={{ color: "var(--text-muted)" }}>Estado</div>
                <div className="flex flex-wrap gap-2">
                  {ESTADOS_COTIZACION.map((e) => (
                    <button key={e} onClick={() => updateEstado(selected.id, e)} className={`yn-btn ${selected.estado === e ? "yn-btn--active-yes" : ""}`}>{e}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {Object.entries(selected).map(([k,v]) => {
                  if (["_id","id","detalles"].includes(k)) return null;
                  if (v === null || v === "" || v === undefined) return null;
                  const val = typeof v === "boolean" ? (v ? "Sí" : "No") : String(v);
                  return (
                    <div key={k}>
                      <div className="text-[11px] uppercase tracking-[0.1em] font-bold" style={{ color: "var(--text-muted)" }}>{k.replace(/_/g, " ")}</div>
                      <div>{val}</div>
                    </div>
                  );
                })}
              </div>
              {selected.detalles && Object.keys(selected.detalles).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-2" style={{ color: "var(--text-muted)" }}>Detalles producto</div>
                  <pre className="text-xs bg-gray-50 p-3 rounded">{JSON.stringify(selected.detalles, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
